import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useApiError } from '../../../shared/errors/useApiError';
import { useNotification } from '../../../shared/notifications/NotificationProvider';
import { useOpportunityMutations } from '../useOpportunities';

interface Props {
  open: boolean;
  postId: string | null;
  onClose: () => void;
}

const REASON_MAX = 500;

/**
 * Modal that captures a free-text reason for flagging a post. Idempotent on
 * the backend — a second click from the same user is silently accepted, so
 * we always show the success toast on any non-error response.
 */
export function ReportPostDialog({ open, postId, onClose }: Props) {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const { report } = useOpportunityMutations();

  const [reason, setReason] = useState('');

  const reasonValid = reason.trim().length > 0 && reason.length <= REASON_MAX;
  const canSubmit = reasonValid && !report.isPending && postId !== null;

  const handleClose = () => {
    if (report.isPending) return;
    setReason('');
    report.reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit || !postId) return;
    try {
      await report.mutateAsync({ id: postId, body: { reason: reason.trim() } });
      notify.success(t('opportunities.report.success'));
      handleClose();
    } catch (err) {
      notify.error(translate(err));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('opportunities.report.dialogTitle')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t('opportunities.report.dialogSubtitle')}
        </DialogContentText>
        {report.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {translate(report.error)}
          </Alert>
        )}
        <TextField
          autoFocus
          label={t('opportunities.report.reasonLabel')}
          placeholder={t('opportunities.report.reasonPlaceholder')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          inputProps={{ maxLength: REASON_MAX }}
          multiline
          minRows={3}
          maxRows={6}
          fullWidth
          required
          disabled={report.isPending}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={report.isPending}>
          {t('opportunities.form.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
          startIcon={report.isPending ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {report.isPending
            ? t('opportunities.report.submitting')
            : t('opportunities.report.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
